import { ApolloClient, InMemoryCache, ApolloLink } from '@apollo/client';
import { ErrorLink } from '@apollo/client/link/error';
import UploadHttpLink from 'apollo-upload-client/UploadHttpLink.mjs';
import { removeAuthenticated } from 'utils/authentication';
import { getStoredSpaceId, clearStoredSpaceId } from 'contexts/space';
import { NotFoundLink, SomethingWrongLink, SpacesLink } from 'utils/link';
import router from 'routes';

const ERROR_CODES = {
  SPACE_NOT_FOUND: 40,
  SPACE_FORBIDDEN: 405,
  NOT_FOUND: 404,
};

const redirectIfNotThere = (path) => {
  if (router.state.location.pathname !== path) {
    router.navigate(path, { replace: true });
  }
};

const createApolloClient = (graphQLServerUrl) => {
  const uploadLink = new UploadHttpLink({
    uri: graphQLServerUrl,
    credentials: 'include',
  });

  const spaceLink = new ApolloLink((operation, forward) => {
    const spaceId = getStoredSpaceId();
    if (spaceId) {
      operation.setContext(({ headers = {} }) => ({
        headers: {
          ...headers,
          'X-Space-Id': spaceId,
        },
      }));
    }
    return forward(operation);
  });

  const errorLink = new ErrorLink(({ result }) => {
    if (!result?.errors) return;

    for (const error of result.errors) {
      if (error.message === 'Please authorize') {
        removeAuthenticated();
        return;
      }

      const code = error.extensions?.code;

      if (code === ERROR_CODES.SPACE_NOT_FOUND) {
        clearStoredSpaceId();
        redirectIfNotThere(SpacesLink());
        return;
      }

      if (code === ERROR_CODES.SPACE_FORBIDDEN) {
        clearStoredSpaceId();
        redirectIfNotThere(NotFoundLink());
        return;
      }

      if (code === ERROR_CODES.NOT_FOUND) {
        redirectIfNotThere(NotFoundLink());
        return;
      }

      if (typeof code === 'number' && code >= 500) {
        redirectIfNotThere(SomethingWrongLink());
        return;
      }
    }
  });

  const client = new ApolloClient({
    link: ApolloLink.from([errorLink, spaceLink, uploadLink]),
    cache: new InMemoryCache({
      typePolicies: {
        Query: {
          fields: {
            space: { merge: true },
            user: { merge: true },
          },
        },
      },
    }),
    defaultOptions: {
      watchQuery: {
        fetchPolicy: 'cache-and-network',
        errorPolicy: 'all',
      },
      query: {
        fetchPolicy: 'cache-first',
        errorPolicy: 'all',
      },
    },
  });

  return client;
};

export default createApolloClient;
